'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlay, faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { PulseButton, Input } from 'src/components/PulseUI';
import { useAppSelector } from 'src/redux/hooks';
import {
  selectSelectedPlay,
  selectEngageMaxOpportunities,
} from 'src/redux/slices/strategyFormSlice';
import { LOCATION } from 'src/hooks/filters/constants';
import { useAiGenerator } from 'src/hooks/aiGenerator/useAiGenerator';
import { useGetAllTeams } from 'src/hooks/teams/useGetAllTeams';
import { useStrategyForm } from 'src/hooks/strategy/useStrategyForm';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import {
  useCreateTargetStrategyMutation,
  useCreateEngageStrategyMutation,
  useCreateChainedStrategyMutation,
} from 'src/api/redux';
import {
  getStrategyConfig,
  StrategyValidationContext,
} from 'src/components/Strategy/config';
import {
  formatCriteriaTemplateWithMetadata,
  getComponentTypeClasses,
  type CriteriaSegment,
} from 'src/utils/criteriaDisplayUtils/criteriaDisplayUtils';
import { STRATEGY_NAMEMAPPING } from 'src/components/Strategy/constants';
import { ConfigCard, SelectTeamsSection } from './components';

interface PreviewStepProps {
  onPublish?: () => void; // Optional - publish is now handled internally
  onBack: () => void;
}

interface SectionHeaderProps {
  number: number;
  title: string;
  subtitle: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  number,
  title,
  subtitle,
}) => (
  <div className="flex items-start gap-3 p-4">
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-600 text-sm font-semibold text-white"
      aria-hidden="true"
    >
      {number}
    </div>
    <div className="flex flex-col gap-1">
      <h3 className="text-base font-semibold text-primary">{title}</h3>
      <p className="text-sm text-secondary">{subtitle}</p>
    </div>
  </div>
);

/**
 * Renders criteria segments with color-coded component values
 */
const CriteriaDisplay: React.FC<{ segments: CriteriaSegment[] }> = ({
  segments,
}) => (
  <div className="text-sm text-secondary leading-relaxed">
    {segments.map((segment, index) => {
      if (segment.isComponent) {
        return (
          <span
            key={index}
            className={`font-medium ${getComponentTypeClasses(
              segment.componentType,
              segment.uiKey
            )}`}
            data-testid={`criteria-component-${segment.componentType}-${index}`}
          >
            {segment.text}
          </span>
        );
      }
      return <span key={index}>{segment.text}</span>;
    })}
  </div>
);

/**
 * Displays Fresh Faces configuration as readable criteria
 */
const FreshFacesCriteriaDisplay: React.FC<{
  freshFacesData: {
    code_set?: string | { id: string; label: string };
    filter_set?: string | { id: string; label: string };
    volume_type?: 'treating' | 'referring';
    provider_type?: 'hcp' | 'site';
  };
}> = ({ freshFacesData }) => {
  const codeSetLabel =
    typeof freshFacesData.code_set === 'object'
      ? freshFacesData.code_set?.label
      : freshFacesData.code_set || 'Not selected';

  const filterSetLabel =
    typeof freshFacesData.filter_set === 'object'
      ? freshFacesData.filter_set?.label
      : freshFacesData.filter_set;

  const volumeType =
    freshFacesData.volume_type === 'referring' ? 'referring' : 'treating';
  const providerType =
    freshFacesData.provider_type === 'site' ? 'sites' : 'providers';

  return (
    <div className="text-sm text-secondary leading-relaxed">
      Show me the up-and-coming{' '}
      <span className="font-medium text-purple-600">{providerType}</span> with
      increasing <span className="font-medium text-sky-600">{volumeType}</span>{' '}
      volume for{' '}
      <span className="font-medium text-pink-600">{codeSetLabel}</span>
      {filterSetLabel && (
        <>
          {' '}
          filtered by{' '}
          <span className="font-medium text-pink-600">{filterSetLabel}</span>
        </>
      )}
      .
    </div>
  );
};

/**
 * Displays Patient Journey configuration as readable criteria
 */
const PatientJourneyCriteriaDisplay: React.FC<{
  indexEventCriteria: {
    criteria_template?: string;
    criteria_inputs?: Record<string, any>;
  } | null;
  targetEventCriteria: {
    criteria_template?: string;
    criteria_inputs?: Record<string, any>;
  } | null;
  patientJourneyData: {
    target_event_interval?: number;
    target_event_direction?: 'BEFORE' | 'AFTER';
    radius?: number;
  };
}> = ({ indexEventCriteria, targetEventCriteria, patientJourneyData }) => {
  const indexSegments = useMemo(() => {
    if (!indexEventCriteria?.criteria_template) return [];
    return formatCriteriaTemplateWithMetadata(
      indexEventCriteria.criteria_template,
      indexEventCriteria.criteria_inputs || {}
    );
  }, [indexEventCriteria]);

  const targetSegments = useMemo(() => {
    if (!targetEventCriteria?.criteria_template) return [];
    return formatCriteriaTemplateWithMetadata(
      targetEventCriteria.criteria_template,
      targetEventCriteria.criteria_inputs || {}
    );
  }, [targetEventCriteria]);

  const interval = patientJourneyData.target_event_interval || 6;
  const radius = patientJourneyData.radius || 0;

  // Normalize direction to display string — API may return 1/2 (numbers) or 'BEFORE'/'AFTER' (strings)
  const directionText = (() => {
    const d = patientJourneyData.target_event_direction as any;
    if (d === 1 || d === 'BEFORE') return 'before';
    if (d === 2 || d === 'AFTER') return 'after';
    return 'after';
  })();

  return (
    <div className="flex flex-col gap-3 text-sm text-secondary leading-relaxed">
      <div>
        <span className="font-medium text-primary">Index Event:</span>{' '}
        {indexSegments.length > 0 ? (
          <CriteriaDisplay segments={indexSegments} />
        ) : (
          <span className="text-tertiary italic">Not configured</span>
        )}
      </div>
      <div>
        <span className="font-medium text-primary">Target Event:</span>{' '}
        {targetSegments.length > 0 ? (
          <CriteriaDisplay segments={targetSegments} />
        ) : (
          <span className="text-tertiary italic">Not configured</span>
        )}
      </div>
      <div className="text-xs text-tertiary">
        Looking {interval} months {directionText} the index event
        {radius > 0 && ` within ${radius} mile radius`}
      </div>
    </div>
  );
};

const PreviewStep: React.FC<PreviewStepProps> = ({ onPublish, onBack }) => {
  const router = useRouter();
  const { setNotification } = useNotification();
  const log = useLogging();

  const selectedPlay = useAppSelector(selectSelectedPlay);
  const engageMaxOpps = useAppSelector(selectEngageMaxOpportunities);
  const { dynamicCriteria: targetCriteria } = useAiGenerator(LOCATION.STRATEGY);
  const { dynamicCriteria: engageCriteria } = useAiGenerator(
    LOCATION.STRATEGY_ENGAGE
  );
  const { dynamicCriteria: indexEventCriteria } = useAiGenerator(
    LOCATION.PATIENT_JOURNEY_INDEX_EVENT
  );
  const { dynamicCriteria: targetEventCriteria } = useAiGenerator(
    LOCATION.PATIENT_JOURNEY_TARGET_EVENT
  );
  const {
    freshFacesData,
    notReferralsData,
    patientJourneyData,
    formData,
    updateLabel,
    resetForm,
  } = useStrategyForm();

  // RTK Query mutations
  const [createTargetStrategy, { isLoading: isCreatingTarget }] =
    useCreateTargetStrategyMutation();
  const [createEngageStrategy, { isLoading: isCreatingEngage }] =
    useCreateEngageStrategyMutation();
  const [createChainedStrategy, { isLoading: isCreatingChain }] =
    useCreateChainedStrategyMutation();

  const isSubmitting = isCreatingTarget || isCreatingEngage || isCreatingChain;

  // Fetch teams from API
  const { data: teamsData, isLoading: isLoadingTeams } = useGetAllTeams();
  const teams = teamsData?.results || [];

  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  // Local state for per-section names and descriptions
  const [targetName, setTargetName] = useState(
    selectedPlay?.target_template?.name || 'Targeting'
  );
  const [targetDescription, setTargetDescription] = useState(
    selectedPlay?.target_template?.description || ''
  );
  const [engageName, setEngageName] = useState(
    selectedPlay?.engage_template?.name || 'Engage'
  );
  const [engageDescription, setEngageDescription] = useState(
    selectedPlay?.engage_template?.description || ''
  );

  // Default the strategy name to the play name if not yet set
  useEffect(() => {
    if (!formData.label && selectedPlay?.name) {
      updateLabel(selectedPlay.name);
    }
  }, [formData.label, selectedPlay?.name, updateLabel]);

  const targetTemplateType = selectedPlay?.target_template?.type;
  const engageTemplateType = selectedPlay?.engage_template?.type;

  // Parse target criteria segments
  const targetSegments = useMemo(() => {
    if (!targetCriteria?.criteria_template) return [];
    return formatCriteriaTemplateWithMetadata(
      targetCriteria.criteria_template,
      targetCriteria.criteria_inputs || {}
    );
  }, [targetCriteria]);

  // Parse engage criteria segments
  const engageSegments = useMemo(() => {
    if (!engageCriteria?.criteria_template) return [];
    return formatCriteriaTemplateWithMetadata(
      engageCriteria.criteria_template,
      engageCriteria.criteria_inputs || {}
    );
  }, [engageCriteria]);

  const toggleTeam = useCallback((teamId: string) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId]
    );
  }, []);

  const canRunPreview = selectedTeams.length > 0;

  // Build validation context for serialization (similar to AddEditStrategy)
  const buildValidationContext = useCallback(
    (
      strategyType: string | undefined,
      label: string,
      description: string,
      isTarget: boolean
    ): StrategyValidationContext => {
      const teamIds = selectedTeams.map((id) => Number(id));

      return {
        formData: {
          label,
          description,
          teams: teamIds,
          refresh_event: isTarget ? formData.refresh_event : 'NEVER',
          max_opportunities: isTarget
            ? formData.max_opportunities
            : engageMaxOpps,
        },
        dynamicCriteria: isTarget ? targetCriteria : engageCriteria,
        freshFacesData,
        notReferralsData,
        patientJourneyData,
        indexEventCriteria,
        targetEventCriteria,
        isEditMode: false,
        isCopyMode: false,
        isEngageStrategy: !isTarget,
        patientJourneyDirection: patientJourneyData?.target_event_direction,
      };
    },
    [
      selectedTeams,
      targetCriteria,
      engageCriteria,
      freshFacesData,
      notReferralsData,
      patientJourneyData,
      indexEventCriteria,
      targetEventCriteria,
      formData.refresh_event,
      formData.max_opportunities,
      engageMaxOpps,
    ]
  );

  // Handle publish - create target strategy, engage strategy, then chain
  const handlePublish = useCallback(async () => {
    if (!selectedPlay) return;

    try {
      let targetStrategyId: number | undefined;
      let engageStrategyId: number | undefined;

      // Step 1: Create target strategy if template exists
      if (selectedPlay.target_template && targetTemplateType) {
        const targetConfig = getStrategyConfig(targetTemplateType);
        if (targetConfig) {
          const targetContext = buildValidationContext(
            targetTemplateType,
            targetName,
            targetDescription,
            true
          );
          const targetPayload = targetConfig.serializeForCreate(targetContext);

          log.event('Strategy Builder: Creating target strategy', {
            type: targetTemplateType,
            label: targetName,
          });

          const targetResult = await createTargetStrategy(
            targetPayload
          ).unwrap();
          targetStrategyId = targetResult.id;

          log.event('Strategy Builder: Target strategy created', {
            id: targetStrategyId,
          });
        }
      }

      // Step 2: Create engage strategy if template exists
      if (selectedPlay.engage_template && engageTemplateType) {
        const engageConfig = getStrategyConfig(engageTemplateType);
        if (engageConfig) {
          const engageContext = buildValidationContext(
            engageTemplateType,
            engageName,
            engageDescription,
            false
          );
          const engagePayload = engageConfig.serializeForCreate(engageContext);

          log.event('Strategy Builder: Creating engage strategy', {
            type: engageTemplateType,
            label: engageName,
          });

          const engageResult = await createEngageStrategy(
            engagePayload
          ).unwrap();
          engageStrategyId = engageResult.id;

          log.event('Strategy Builder: Engage strategy created', {
            id: engageStrategyId,
          });
        }
      }

      // Step 3: Create chain linking the strategies
      const chainPayload = {
        label: formData.label || `${selectedPlay.name} Chain`,
        description:
          formData.description ||
          `Chain created from ${selectedPlay.name} play`,
        target_strategy: targetStrategyId,
        engage_strategies: engageStrategyId ? [engageStrategyId] : [],
        teams: selectedTeams.map((id) => Number(id)),
        notifications_enabled: true,
      };

      log.event('Strategy Builder: Creating chain', {
        label: chainPayload.label,
        targetStrategyId,
        engageStrategyId,
      });

      const chainResult = await createChainedStrategy(chainPayload).unwrap();

      log.event('Strategy Builder: Chain created successfully', {
        chainId: chainResult.id,
      });

      setNotification({
        title: 'Strategy Published',
        message: `${
          formData.label || selectedPlay.name
        } has been created successfully.`,
        type: 'success',
      });

      // Reset form and navigate to strategy manager
      resetForm();
      router.push('/settings/admin/strategy-manager/');
    } catch (error: any) {
      log.exception('Strategy Builder: Error publishing strategy');

      const errorMessage =
        error?.data?.error_message ||
        error?.message ||
        'Failed to publish strategy';

      setNotification({
        title: 'Error Publishing Strategy',
        message: errorMessage,
        type: 'error',
      });
    }
  }, [
    selectedPlay,
    targetTemplateType,
    engageTemplateType,
    targetName,
    targetDescription,
    engageName,
    engageDescription,
    formData,
    selectedTeams,
    buildValidationContext,
    createTargetStrategy,
    createEngageStrategy,
    createChainedStrategy,
    setNotification,
    resetForm,
    router,
    log,
  ]);

  // Render targeting criteria based on type
  const renderTargetCriteria = () => {
    if (targetTemplateType === 'FRESH_FACES') {
      return <FreshFacesCriteriaDisplay freshFacesData={freshFacesData} />;
    }

    if (targetSegments.length > 0) {
      return <CriteriaDisplay segments={targetSegments} />;
    }

    return (
      <p className="text-sm text-tertiary italic">
        No targeting criteria configured
      </p>
    );
  };

  // Render engage criteria based on type
  const renderEngageCriteria = () => {
    if (engageTemplateType === 'PATIENT_JOURNEY') {
      return (
        <PatientJourneyCriteriaDisplay
          indexEventCriteria={indexEventCriteria}
          targetEventCriteria={targetEventCriteria}
          patientJourneyData={patientJourneyData}
        />
      );
    }

    if (engageSegments.length > 0) {
      return (
        <>
          <CriteriaDisplay segments={engageSegments} />
          {engageTemplateType === 'NOT_REFERRING' &&
            notReferralsData?.radius > 0 && (
              <div className="mt-2 text-xs text-tertiary">
                Within {notReferralsData.radius} mile radius
              </div>
            )}
        </>
      );
    }

    return (
      <p className="text-sm text-tertiary italic">
        No engage criteria configured
      </p>
    );
  };

  const getStrategyTypeLabel = (type?: string) => {
    if (!type) return '';
    return (
      STRATEGY_NAMEMAPPING[type as keyof typeof STRATEGY_NAMEMAPPING] || type
    );
  };

  return (
    <div className="flex h-[calc(100vh-22rem)] flex-col">
      <main
        className="flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto rounded-lg border border-neutral-200 bg-white p-6"
        aria-label="Preview and publish"
        data-testid="preview-main"
      >
        {/* Page Header */}
        <div className="flex flex-col gap-2 text-center">
          <h1
            className="text-xl font-bold text-primary"
            data-testid="preview-title"
          >
            Preview & Publish
          </h1>
          <p className="text-sm text-secondary">
            Review your strategy configuration, then run a preview to see
            results
          </p>
        </div>

        {/* Section 1: Strategy Details */}
        <section
          className="rounded-lg border border-neutral-200"
          aria-labelledby="section-1-title"
        >
          <SectionHeader
            number={1}
            title="Strategy Details"
            subtitle="Name and describe your strategy"
          />
          <div className="flex flex-col gap-4 border-t border-neutral-200 p-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="strategy-name"
                className="text-sm font-bold text-primary font-['Inter']"
              >
                Strategy Name
              </label>
              <Input
                id="strategy-name"
                type="text"
                value={formData.label}
                onChange={(e) => updateLabel(e.target.value)}
                placeholder="Enter a name for this strategy..."
                fullWidth
                data-testid="strategy-name-input"
              />
            </div>
            {/* <div className="flex flex-col gap-1">
              <label
                htmlFor="strategy-description"
                className="text-sm font-medium text-primary"
              >
                Strategy Description
              </label>
              <textarea
                id="strategy-description"
                value={formData.description}
                onChange={(e) => updateDescription(e.target.value)}
                placeholder="Describe what this strategy does..."
                rows={2}
                className="w-full resize-y rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                data-testid="strategy-description-input"
              />
            </div> */}
          </div>
        </section>

        {/* Section 2: Strategy Configuration */}
        <section
          className="rounded-lg border border-neutral-200"
          aria-labelledby="section-2-title"
        >
          <SectionHeader
            number={2}
            title="Strategy Configuration"
            subtitle={
              selectedPlay?.engage_template
                ? 'Review your targeting and engage criteria'
                : 'Review your targeting criteria'
            }
          />
          <div className="flex flex-col gap-6 border-t border-neutral-200 p-4">
            {/* Targeting */}
            {selectedPlay?.target_template && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-primary">
                    Targeting
                    {targetTemplateType && (
                      <span className="ml-2 text-xs font-normal text-tertiary">
                        ({getStrategyTypeLabel(targetTemplateType)})
                      </span>
                    )}
                  </h4>
                  <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-link hover:underline"
                    data-testid="edit-targeting-link"
                  >
                    Edit
                  </button>
                </div>
                <ConfigCard
                  title={targetName}
                  criteriaContent={renderTargetCriteria()}
                  inputLabel="Targeting title"
                  inputValue={targetName}
                  textareaValue={targetDescription}
                  onInputChange={setTargetName}
                  onTextareaChange={setTargetDescription}
                />
              </div>
            )}

            {/* Engage */}
            {selectedPlay?.engage_template && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-primary">
                    Engage
                    {engageTemplateType && (
                      <span className="ml-2 text-xs font-normal text-tertiary">
                        ({getStrategyTypeLabel(engageTemplateType)})
                      </span>
                    )}
                  </h4>
                  <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-link hover:underline"
                    data-testid="edit-engage-link"
                  >
                    Edit
                  </button>
                </div>
                <ConfigCard
                  tag={
                    engageTemplateType === 'REFERRALS' ? 'Referral' : undefined
                  }
                  title={engageName}
                  criteriaContent={renderEngageCriteria()}
                  inputLabel="Engage title"
                  inputValue={engageName}
                  textareaValue={engageDescription}
                  onInputChange={setEngageName}
                  onTextareaChange={setEngageDescription}
                />
              </div>
            )}
          </div>
        </section>

        {/* Section 3: Select Teams */}
        <section
          className="rounded-lg border border-neutral-200"
          aria-labelledby="section-3-title"
        >
          <SectionHeader
            number={3}
            title="Select Teams"
            subtitle="Choose the teams to run this strategy against"
          />
          <div className="border-t border-neutral-200 p-4">
            <SelectTeamsSection
              teams={teams}
              isLoading={isLoadingTeams}
              selectedTeams={selectedTeams}
              onToggleTeam={toggleTeam}
            />
          </div>
        </section>

        {/* Footer Actions */}
        <div className="flex flex-wrap items-center gap-4 border-t border-neutral-200 pt-6">
          <PulseButton
            variant="solid"
            color="primary"
            onClick={handlePublish}
            disabled={!canRunPreview}
            loading={isSubmitting}
            startDecorator={faPlay}
            data-testid="run-preview-button"
            aria-label="Run Preview"
          >
            Run Preview
          </PulseButton>

          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="flex items-center gap-2 text-sm font-medium text-link hover:underline disabled:opacity-50"
            data-testid="back-to-edit-link"
          >
            <Icon icon={faArrowLeft} size="xs" />
            Back to Edit
          </button>

          {!canRunPreview && !isSubmitting && (
            <span
              className="text-sm text-red-500"
              role="alert"
              data-testid="team-validation-message"
            >
              Select at least one team to run preview
            </span>
          )}
        </div>
      </main>
    </div>
  );
};

export default PreviewStep;
